// ══════════════════════════════════════════════════════════════════════════════
// PAGE MESSAGES - Style harmonisé avec Liste de tâches
// AbracadaWorld - Cockpit Particulier
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Send, MoreVertical, Phone, Info, Image, Paperclip,
  Check, CheckCheck, Clock, Star, Archive, Trash2, ChevronLeft, 
  MessageCircle, Plus, Filter, Users, User, X, Sparkles, ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Liste des conversations
// ══════════════════════════════════════════════════════════════════════════════
function ConversationList({ conversations, selectedId, onSelect, isDarkMode }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = conversations.filter(c => 
    c.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.annonceTitre.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col">
      {/* Search */}
      <div className="p-3">
        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Rechercher une conversation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`flex-1 bg-transparent border-0 outline-none text-xs placeholder-gray-400 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
          />
        </div>
      </div>

      {/* Conversations */}
      <div className="flex-1 overflow-y-auto">
        {filtered.map((conv) => (
          <button
            key={conv.id}
            onClick={() => onSelect(conv)}
            className={`w-full p-3 flex items-start gap-3 border-b transition-colors ${
              isDarkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'
            } ${selectedId === conv.id ? (isDarkMode ? 'bg-slate-700' : 'bg-orange-50') : ''}`}
          >
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-11 h-11 bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {conv.userName.charAt(0)}
              </div>
              {conv.isOnline && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center justify-between mb-0.5">
                <h4 className={`font-semibold text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {conv.userName}
                </h4>
                <span className={`text-[11px] flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {conv.lastMessageTime}
                </span>
              </div>
              <p className="text-xs text-orange-600 truncate mb-0.5">{conv.annonceTitre}</p>
              <p className={`text-xs truncate ${
                conv.unread 
                  ? (isDarkMode ? 'text-white font-medium' : 'text-gray-900 font-medium') 
                  : (isDarkMode ? 'text-gray-400' : 'text-gray-500')
              }`}>
                {conv.lastMessage}
              </p>
            </div>

            {/* Unread badge */}
            {conv.unread > 0 && (
              <span className="w-5 h-5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0">
                {conv.unread}
              </span>
            )}
          </button>
        ))}

        {filtered.length === 0 && (
          <div className={`p-8 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <Search size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Aucune conversation trouvée</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Zone de chat
// ══════════════════════════════════════════════════════════════════════════════
function ChatArea({ conversation, onSendMessage, onBack, isDarkMode }) {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation?.messages]);

  const handleSend = () => {
    if (!message.trim()) return;
    onSendMessage(message);
    setMessage('');
  };

  if (!conversation) {
    return (
      <div className={`h-full flex items-center justify-center ${isDarkMode ? 'bg-slate-800' : 'bg-[#faf9f7]'}`}>
        <div className="text-center p-8">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
            isDarkMode ? 'bg-slate-700' : 'bg-gray-100'
          }`}>
            <MessageCircle size={28} className={isDarkMode ? 'text-gray-500' : 'text-gray-400'} />
          </div>
          <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Sélectionne une conversation
          </p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            pour voir les messages
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`px-4 py-3 border-b flex items-center gap-3 ${
        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
      }`}>
        <button onClick={onBack} className={`lg:hidden p-2 rounded-lg ${
          isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
        }`}>
          <ChevronLeft size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />
        </button>
        <div className="relative">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {conversation.userName.charAt(0)}
          </div>
          {conversation.isOnline && (
            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {conversation.userName}
          </h3>
          <p className={`text-xs truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {conversation.annonceTitre}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>
            <Phone size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
          <button className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>
            <MoreVertical size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
        </div>
      </div>

      {/* Annonce preview */}
      {conversation.annonceImage && (
        <div className={`px-4 py-2 border-b ${
          isDarkMode ? 'bg-slate-700/50 border-slate-700' : 'bg-orange-50/50 border-orange-100'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-xl overflow-hidden flex-shrink-0">
              <img src={conversation.annonceImage} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {conversation.annonceTitre}
              </p>
              <p className="text-sm font-bold text-orange-600">{conversation.annoncePrix} €</p>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${
        isDarkMode ? 'bg-slate-900/50' : 'bg-[#faf9f7]'
      }`}>
        {conversation.messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.isMe ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[75%] ${msg.isMe ? 'order-2' : ''}`}>
              <div
                className={`px-4 py-2.5 rounded-2xl text-sm ${
                  msg.isMe
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-br-md'
                    : isDarkMode 
                      ? 'bg-slate-700 text-white rounded-bl-md' 
                      : 'bg-white text-gray-800 rounded-bl-md shadow-sm'
                }`}
              >
                {msg.text}
              </div>
              <div className={`flex items-center gap-1 mt-1 ${msg.isMe ? 'justify-end' : ''}`}>
                <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                  {msg.time}
                </span>
                {msg.isMe && (
                  msg.read 
                    ? <CheckCheck size={12} className="text-orange-500" />
                    : <Check size={12} className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} />
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={`p-3 border-t ${
        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
      }`}>
        <div className="flex items-center gap-2">
          <button className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>
            <Paperclip size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
          <button className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>
            <Image size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Écrire un message..."
            className={`flex-1 px-4 py-2 text-sm rounded-xl border-0 focus:ring-2 focus:ring-orange-500 ${
              isDarkMode ? 'bg-slate-700 text-white placeholder-gray-400' : 'bg-gray-100 text-gray-900'
            }`}
          />
          <button
            onClick={handleSend}
            disabled={!message.trim()}
            className={`p-2.5 rounded-xl transition-all ${
              message.trim()
                ? 'bg-orange-500 text-white hover:bg-orange-600'
                : isDarkMode ? 'bg-slate-700 text-gray-500' : 'bg-gray-100 text-gray-400'
            }`}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════════════════
export default function MessagesPage({ onAskAbra, onOpenMenu, onBackToHub, isDarkMode: propDarkMode, toggleTheme }) {
  const { user, token } = useCurrentUser();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [activeFilter, setActiveFilter] = useState('Toutes');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchContact, setSearchContact] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isDarkMode = propDarkMode ?? false;
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filters = ['Toutes', 'Non lues', 'Groupes', 'Pros'];

  // Charger les contacts quand la modale s'ouvre
  useEffect(() => {
    if (showCreateModal && token) {
      loadContacts();
    }
  }, [showCreateModal, token]);

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      const response = await fetch(`${API_URL}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      // Contacts de démo si pas d'API
      setContacts([
        { id: '1', name: 'Marie Dupont', email: 'marie@example.com', phone: '06 12 34 56 78' },
        { id: '2', name: 'Pierre Martin', email: 'pierre@example.com', phone: '06 98 76 54 32' },
        { id: '3', name: 'Sophie Bernard', email: 'sophie@example.com', phone: '06 11 22 33 44' },
      ]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const filteredContacts = contacts.filter(c => 
    c.name?.toLowerCase().includes(searchContact.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchContact.toLowerCase())
  );

  const handleSendNewMessage = () => {
    if (!selectedContact || !newMessage.trim()) {
      toast.error('Sélectionne un contact et écris un message');
      return;
    }

    // Créer une nouvelle conversation
    const newConv = {
      id: `new-${Date.now()}`,
      userName: selectedContact.name,
      userEmail: selectedContact.email,
      isOnline: false,
      annonceTitre: 'Conversation directe',
      lastMessage: newMessage,
      lastMessageTime: 'À l\'instant',
      unread: 0,
      messages: [{
        text: newMessage,
        isMe: true,
        time: 'À l\'instant',
        read: false,
      }]
    };

    setConversations(prev => [newConv, ...prev]);
    setSelectedConversation(newConv);
    setShowChat(true);
    setShowCreateModal(false);
    setSelectedContact(null);
    setNewMessage('');
    setSearchContact('');
    toast.success('Message envoyé !');
  };

  // Données démo
  const DEMO_CONVERSATIONS = [];

  useEffect(() => {
    if (user?.isDemo) {
      setConversations(DEMO_CONVERSATIONS);
    } else {
      setConversations([]);
    }
  }, [user]);

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setShowChat(true);
    setConversations(prev => prev.map(c => 
      c.id === conv.id ? { ...c, unread: 0 } : c
    ));
  };

  const handleSendMessage = (text) => {
    if (!selectedConversation) return;
    
    const newMsg = {
      text,
      isMe: true,
      time: 'À l\'instant',
      read: false,
    };

    setConversations(prev => prev.map(c => 
      c.id === selectedConversation.id 
        ? { 
            ...c, 
            messages: [...c.messages, newMsg],
            lastMessage: text,
            lastMessageTime: 'À l\'instant'
          } 
        : c
    ));

    setSelectedConversation(prev => ({
      ...prev,
      messages: [...prev.messages, newMsg]
    }));
  };

  const unreadCount = conversations.reduce((acc, c) => acc + (c.unread || 0), 0);

  // Filtrage des conversations selon le filtre actif
  const filteredConversations = conversations.filter(conv => {
    if (activeFilter === 'Toutes') return true;
    if (activeFilter === 'Non lues') return conv.unread > 0;
    if (activeFilter === 'Groupes') return conv.isGroup;
    if (activeFilter === 'Pros') return conv.isPro;
    return true;
  });

  // Contenu de la page (utilisé pour desktop et mobile)
  const pageContent = (
    <div className="h-full flex flex-col">
      {/* Header - Style Liste de tâches */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
              Mes messages
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Toutes tes conversations au même endroit
            </p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            data-testid="create-message-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Nouveau message</span>
            <span className="sm:hidden">Nouveau</span>
          </Button>
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-4 mt-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
          }`}>
            <MessageCircle size={14} className="text-orange-500" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {conversations.length} conversation{conversations.length > 1 ? 's' : ''}
            </span>
          </div>
          {unreadCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-50 border border-orange-100">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-orange-700">
                {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Filtres - Style Liste de tâches */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
        {filters.map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              activeFilter === filter
                ? 'bg-orange-500 text-white shadow-sm'
                : (isDarkMode 
                    ? 'bg-slate-800 text-gray-300 hover:bg-slate-700' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-100')
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      {/* Contenu principal */}
      {conversations.length === 0 ? (
        /* État vide - Style harmonisé */
        <div className={`flex-1 rounded-2xl flex items-center justify-center ${
          isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
        }`}>
          <div className="text-center p-8 max-w-sm">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
              isDarkMode ? 'bg-slate-700' : 'bg-gray-50'
            }`}>
              <MessageCircle size={28} className={isDarkMode ? 'text-gray-500' : 'text-gray-300'} />
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Aucune conversation
            </h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Tes échanges apparaîtront ici quand tu contacteras un vendeur ou qu&apos;un acheteur te contactera.
            </p>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2"
            >
              <Plus size={16} />
              Nouveau message
            </Button>
          </div>
        </div>
      ) : (
        /* Liste + Chat */
        <div className={`flex-1 rounded-2xl overflow-hidden flex ${
          isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
        }`}>
          {/* Sidebar conversations - masqué sur mobile si chat ouvert */}
          <div className={`${showChat ? 'hidden md:flex' : 'flex'} w-full md:w-80 border-r ${
            isDarkMode ? 'border-slate-700' : 'border-gray-100'
          } flex-col`}>
            <ConversationList
              conversations={filteredConversations}
              selectedId={selectedConversation?.id}
              onSelect={(conv) => {
                setSelectedConversation(conv);
                setShowChat(true);
              }}
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Zone chat */}
          <div className={`${showChat ? 'flex' : 'hidden md:flex'} flex-1 flex-col`}>
            {selectedConversation ? (
              <ChatArea
                conversation={selectedConversation}
                onSendMessage={handleSendMessage}
                onBack={() => setShowChat(false)}
                isDarkMode={isDarkMode}
              />
            ) : (
              /* État vide chat */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle size={48} className={isDarkMode ? 'text-slate-600 mx-auto mb-4' : 'text-gray-200 mx-auto mb-4'} />
                  <p className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                    Sélectionne une conversation
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal création conversation */}
      <AnimatePresence>
        {showCreateModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 md:left-[288px] bg-black/50 z-[10002]"
              onClick={() => setShowCreateModal(false)}
            />
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`fixed left-4 right-4 md:left-1/2 md:-translate-x-1/2 top-1/2 -translate-y-1/2 md:w-[480px] max-h-[80vh] rounded-2xl shadow-2xl z-[10003] flex flex-col ${
                isDarkMode ? 'bg-slate-800' : 'bg-white'
              }`}
            >
              {/* Header modal */}
              <div className={`flex items-center justify-between p-4 border-b ${
                isDarkMode ? 'border-slate-700' : 'border-gray-100'
              }`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                    <MessageCircle size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Nouveau message
                    </h2>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Choisis un contact
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                </button>
              </div>

              {/* Corps modal */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Recherche */}
                <div className="relative mb-4">
                  <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                  <input
                    type="text"
                    value={searchContact}
                    onChange={(e) => setSearchContact(e.target.value)}
                    placeholder="Rechercher un contact..."
                    className={`w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border-0 ${
                      isDarkMode 
                        ? 'bg-slate-700 text-white placeholder-gray-400' 
                        : 'bg-gray-100 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                </div>

                {/* Liste contacts */}
                {loadingContacts ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full" />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-8">
                    <Users size={32} className={isDarkMode ? 'text-gray-600 mx-auto mb-2' : 'text-gray-300 mx-auto mb-2'} />
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Aucun contact disponible
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {contacts
                      .filter(c => 
                        `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchContact.toLowerCase())
                      )
                      .map(contact => (
                        <button
                          key={contact._id}
                          onClick={() => setSelectedContact(contact)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                            selectedContact?._id === contact._id
                              ? 'bg-orange-50 border-2 border-orange-500'
                              : (isDarkMode 
                                  ? 'bg-slate-700 hover:bg-slate-600' 
                                  : 'bg-gray-50 hover:bg-gray-100')
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-semibold text-sm">
                            {contact.firstName?.[0]}{contact.lastName?.[0]}
                          </div>
                          <div className="flex-1 text-left">
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {contact.firstName} {contact.lastName}
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {contact.email || contact.phone || contact.contactType || 'Contact'}
                            </p>
                          </div>
                          {selectedContact?._id === contact._id && (
                            <Check size={18} className="text-orange-500" />
                          )}
                        </button>
                      ))}
                  </div>
                )}
              </div>

              {/* Footer modal - Message */}
              {selectedContact && (
                <div className={`p-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                  <div className={`mb-3 p-2 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-orange-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-orange-700'}`}>
                      À : <span className="font-medium">{selectedContact.firstName} {selectedContact.lastName}</span>
                    </p>
                  </div>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écris ton message..."
                    rows={3}
                    className={`w-full px-3 py-2 rounded-xl text-sm resize-none border-0 ${
                      isDarkMode 
                        ? 'bg-slate-700 text-white placeholder-gray-400' 
                        : 'bg-gray-100 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                  <div className="flex gap-2 mt-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowCreateModal(false)}
                      className={`flex-1 ${isDarkMode ? 'border-slate-600' : ''}`}
                    >
                      Annuler
                    </Button>
                    <Button 
                      onClick={handleSendNewMessage}
                      disabled={!newMessage.trim()}
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                    >
                      <Send size={16} className="mr-2" />
                      Envoyer
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );

  // Version mobile fullscreen (quand on vient du TopBanner)
  if (isMobile && onBackToHub) {
    return (
      <div className="fixed inset-0 z-[10002] bg-white flex flex-col">
        {/* Header mobile avec flèche retour */}
        <header 
          className="flex items-center gap-2 px-3 bg-white border-b border-gray-100 flex-shrink-0"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
        >
          <button onClick={onBackToHub} className="p-1">
            <ArrowLeft size={22} className="text-gray-700" />
          </button>
          <span className="text-[16px] font-bold text-gray-900">Mes messages</span>
        </header>

        {/* Contenu original de la page */}
        <div className="flex-1 overflow-y-auto p-4">
          {pageContent}
        </div>
      </div>
    );
  }

  // Version normale (dans le cockpit)
  return pageContent;
}
